import store from '@/store'

const $private = Symbol('private method')
function assertPrivate(v) {
  if (v !== $private) throw new Error('Private method accessed')
}

function quickHash(s) {
  let h = 1
  for (let i = 0; i < s.length; i++) {
    h = Math.imul((h + s.charCodeAt(i)) | 0, 2654435761)
  }
  return (h ^ (h >>> 17)) >>> 0
}

function parseNumber(text, default_, max) {
  const value = typeof text === 'number' ? text : Number(text)
  if (isNaN(value)) return default_
  if (value < 0) return 0
  if (value > max) return max
  return value
}

function hue2rgb(p, q, hue) {
  if (hue < 0) hue += 360
  if (hue > 360) hue -= 360
  if (hue < 60) return p + ((q - p) * hue) / 60
  if (hue < 180) return q
  if (hue < 240) return p + ((q - p) * (240 - hue)) / 60
  return p
}

export default class Color {
  static forString(str) {
    return Color.forIndex(quickHash(str))
  }

  static forIndex(index) {
    return new HSLColor((index % 20) * 18, 0.7, 0.6)
  }

  static from(value) {
    if (value instanceof Color) {
      return value
    }

    if (Array.isArray(value)) {
      return new RGBColor(...value)
    }

    if (typeof value === 'string') {
      if (value.startsWith('#')) {
        return new RGBColor(
          ...value.match(/[0-9a-f]{2}/gi).map((m, index) => {
            const value = parseInt(m, 16)
            return index === 3 ? value / 255 : value
          }),
        )
      }

      if (value.startsWith('rgb(') || value.startsWith('rgba(')) {
        return new RGBColor(...value.match(/[0-9.]+/g).map(m => parseFloat(m, 10)))
      }

      if (value.startsWith('hsl(') || value.startsWith('hsla(')) {
        return new HSLColor(
          ...value.match(/[0-9.]+/g).map((m, index) => {
            const value = parseFloat(m, 10)
            if (index === 1 || index === 2) return value / 100
            return value
          }),
        )
      }
    }

    throw new Error(`Failed to parse value ${value}`)
  }

  constructor($private) {
    assertPrivate($private)
  }

  darken(amount) {
    const { h, s, l, a } = this.asHSL()
    return new HSLColor(h, s, l - amount, a)
  }

  lighten(amount) {
    return this.darken(-amount)
  }

  mix(other, percent) {
    const self = this.asRGB()
    other = Color.from(other).asRGB()
    return new RGBColor(
      self.r * (1 - percent) + other.r * percent,
      self.g * (1 - percent) + other.g * percent,
      self.b * (1 - percent) + other.b * percent,
      self.a * (1 - percent) + other.a * percent,
    )
  }

  asBackground(percent = 1) {
    return this.mix(store.state.theme === 'dark' ? '#000000' : '#ffffff', 0.4 * percent)
  }

  asForeground(percent = 1) {
    return this.mix(store.state.theme === 'dark' ? '#ffffff' : '#000000', 0.4 * percent)
  }

  // This is a hack to be compateble with Vuetify Colorable mixin
  match(re) {
    return 'rgb(0, 0, 0)'.match(re)
  }

  toHexString() {
    const { r, g, b, a } = this.asRGB()
    return (
      '#' +
      [r, g, b, a]
        .map((value, index) => {
          if (index === 3 && value === 1) return ''
          return (value < 16 ? '0' : '') + (value | 0).toString(16)
        })
        .join('')
    )
  }
}

export class RGBColor extends Color {
  constructor(r, g, b, a = 1) {
    super($private)
    this.r = parseNumber(r, 0, 255)
    this.g = parseNumber(g, 0, 255)
    this.b = parseNumber(b, 0, 255)
    this.a = parseNumber(a, 1, 1)
  }

  cloneWith({ r = this.r, g = this.g, b = this.b, a = this.a }) {
    return new RGBColor(r, g, b, a)
  }

  asRGB() {
    return this
  }

  asHSL() {
    let { r, g, b, a } = this
    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h, s
    const l = (max + min) / 2

    if (max === min) {
      h = s = 0 // achromatic
    } else {
      var d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }

      h *= 60
    }

    return new HSLColor(h, s, l, a)
  }

  toString() {
    const rgb = `${this.r}, ${this.g}, ${this.b}`
    return this.a !== 1 ? `rgba(${rgb}, ${this.a})` : `rgb(${rgb})`
  }
}

export class HSLColor extends Color {
  constructor(h, s, l, a = 1) {
    super($private)
    this.h = parseNumber(h, 0, 360)
    this.s = parseNumber(s, 0, 1)
    this.l = parseNumber(l, 0, 1)
    this.a = parseNumber(a, 1, 1)
  }

  cloneWith({ h = this.h, s = this.s, l = this.l, a = this.a }) {
    return new HSLColor(h, s, l, a)
  }

  asRGB() {
    const { h, s, l, a } = this
    let r, g, b
    if (s === 0) {
      r = g = b = l
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h + 120)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 120)
    }
    return new RGBColor(r * 255, g * 255, b * 255, a)
  }

  asHSL() {
    return this
  }

  toString() {
    const hsl = `${this.h}, ${this.s * 100}%, ${this.l * 100}%`
    return this.a !== 1 ? `hsla(${hsl}, ${this.a})` : `hsl(${hsl})`
  }
}

export function interpolate(palette) {
  // Precompute RGB colors
  palette = palette.map(color => Color.from(color).asRGB())

  return t => {
    const idx = (palette.length - 1) * t
    const lIdx = Math.floor(idx)
    const rIdx = Math.ceil(idx)
    return palette[lIdx].mix(palette[rIdx], idx - lIdx)
  }
}

export function getPalette(nbr) {
  const defaultColors = [
    '#bd93f9',
    '#8be9fd',
    '#ffb86c',
    '#ff79c6',
    '#50fa7b',
    '#ff5555',
    '#f1fa8c',
    '#6272a4',
  ]

  return defaultColors.slice(0, nbr)
}
